import React, { useMemo, lazy, Suspense } from "react";
import { FaEnvelope, FaMobileAlt } from "react-icons/fa";
import { FaLocationArrow } from "react-icons/fa6";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";

// Lazy load sticky footer only on homepage
const StickyFooter = lazy(() => import("./StickyFooter"));

const Footer = () => {
    const { pathname } = useLocation();
    const menuListData = useSelector((state) => state.webSetting.menuListData);
    const companyData = useSelector((state) => state.webSetting.companyData);

    const description = useMemo(() => companyData?.description, [companyData?.description]);
    const logoSrc = useMemo(() => companyData?.logo, [companyData?.logo]);

    const { quickLinks, importantLinks } = useMemo(() => {
        const quickLinks = menuListData?.filter((data) => data.footer_menu === "1");
        const importantLinks = menuListData?.filter((data) => data.second_footer_menu === "1");
        return { quickLinks, importantLinks };
    }, [menuListData]);

    const RenderLinks = ({ title, links }) => {
        if (!links?.length) return null;
        return (
            <div className="py-8 px-4">
                <h2 className="text-xl font-bold sm:text-left mb-3">{title}</h2>
                <ul className="space-y-3">
                    {links.map((data, index) => (
                        <li key={index}>
                            <Link
                                to={data.url}
                                className="text-gray dark:text-gray font-semibold hover:text-primary duration-300"
                            >
                                {data.name}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    return (
        <>
            <footer className="bg-light" aria-label="Site Footer">
                    <div className="grid md:grid-cols-3 ">
                        {/* Logo and Description */}
                        <div className="py-8 px-4">
                            <div className="w-24 aspect-[4/3] overflow-hidden shrink-0">
                                <img
                                    src={logoSrc}
                                    width="96"
                                    height="72"
                                    decoding="async"
                                    fetchpriority="low"
                                    alt={`${companyData?.name} footer logo - Explore STEMCity | Educational Kit | Robotics | STEM Learning | DIY Project`}
                                    title={`${companyData?.name} footer logo - Explore STEMCity | Educational Kit | Robotics | STEM Learning | DIY Project`}
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <p className="text-black text-lg font-bold dark:text-gray lg:pr-24 pt-3 min-h-[60px]">
                                Changing the way of learning...
                            </p>
                            <p className="text-black font-semibold dark:text-gray lg:pr-24 pt-3 min-h-[60px]">
                                {description || 'Loading...'}
                            </p>
                        </div>

                        {/* Links */}
                        <div className="col-span-2 grid grid-cols-2 sm:grid-cols-3 md:pl-10">
                            <RenderLinks title="Quick Links" links={quickLinks} />
                            <RenderLinks title="Important Links" links={importantLinks} />

                            {/* Contact Information */}
                            <div className="py-8 px-4 col-span-2 sm:col-auto">
                                <h2 className="text-xl font-bold sm:text-left mb-3">Contact</h2>
                                <div className="space-y-6">
                                    {companyData?.address && (
                                        <Link to={companyData?.map_link || "#"}>
                                            <div className="flex items-center gap-3">
                                                <FaLocationArrow className="text-gray font-semibold hover:text-primary" size={20}/>
                                                <p className="text-gray font-semibold hover:text-primary">
                                                    {companyData?.address}
                                                </p>
                                            </div>
                                        </Link>
                                    )}
                                    {companyData?.phone && (
                                        <Link to={`tel:${companyData?.phone}`}>
                                            <div className="flex items-center gap-3">
                                                <FaMobileAlt className="text-gray font-semibold hover:text-primary" />
                                                <p className="text-gray font-semibold hover:text-primary">
                                                    {companyData?.phone}
                                                </p>
                                            </div>
                                        </Link>
                                    )}
                                    {companyData?.email && (
                                        <Link to={`mailto:${companyData?.email}`}>
                                            <div className="flex items-center gap-3">
                                                <FaEnvelope className="text-gray font-semibold hover:text-primary" size={20}/>
                                                <p className="text-gray font-semibold hover:text-primary">
                                                    {companyData?.email}
                                                </p>
                                            </div>
                                        </Link>
                                    )}
                                    {companyData?.landline && (
                                        <Link to={`tel:${companyData?.landline}`}>
                                            <div className="flex items-center gap-3">
                                                <FaMobileAlt className="text-gray font-semibold hover:text-primary" size={20} />
                                                <p className="text-gray font-semibold hover:text-primary">
                                                    {companyData?.landline}
                                                </p>
                                            </div>
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
            </footer>

            {/* Lazy load sticky footer only on homepage */}
            {pathname === '/' && (
                <Suspense fallback={<div style={{ height: '60px' }} />}>
                    <StickyFooter />
                </Suspense>
            )}
        </>
    );
};

export default React.memo(Footer);
